import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import React from "react";
import { HiOutlineCog, HiOutlineGlobeAlt, HiOutlineLightBulb, HiOutlineRefresh } from 'react-icons/hi';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { ActiveTag, Tag } from "../types";

export const renderMarkdownContent = (content: string, isStreaming = false) => {
  if (!content) return null;

  // Clean the content by removing thinking tags
  let cleanContent = content;
  cleanContent = cleanContent.replace(/<\/?thinking>/g, '');

  // Clean up Unicode checkboxes but keep markdown ones for processing
  cleanContent = cleanContent.replace(/^\s*[\□☑✓]\s*/gm, '');

  // Parse and render the content with custom checkboxes
  const parseContentWithCheckboxes = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];

    lines.forEach((line, index) => {
      // Match checkboxes at the beginning of lines or after numbers/bullets
      const checkboxMatch = line.match(/^(\s*)(\d+\.\s*|\*\s*|-\s*)?(\[[x\sX]\])\s*(.*)$/);

      if (checkboxMatch) {
        const [, indent, prefix, checkbox, rest] = checkboxMatch;
        const isChecked = checkbox.toLowerCase() === '[x]';

        elements.push(
          <div key={`checkbox-${index}`} className="checkbox-item" style={{ marginLeft: indent.length * 8 }}>
            {prefix && <span className="checkbox-prefix">{prefix}</span>}
            <div className={`custom-checkbox ${isChecked ? 'checked' : ''}`}></div>
            <div className="checkbox-text">
              {rest}
            </div>
          </div>
        );
      } else if (line.trim()) {
        elements.push(
          <div key={`content-${index}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                mark: ({ children, ...props }) => (
                  <mark className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded text-gray-900 dark:text-gray-100" {...props}>
                    {children}
                  </mark>
                )
              }}
            >
              {line}
            </ReactMarkdown>
          </div>
        );
      } else if (line === '') {
        elements.push(<div key={`empty-${index}`} style={{ height: '8px' }}></div>);
      }
    });

    return elements;
  };

  // Check if content has markdown checkboxes
  if (/\[[x\sX]\]/.test(cleanContent)) {
    return (
      <div className="markdown-content">
        {parseContentWithCheckboxes(cleanContent)}
        {isStreaming && <span className="thinking-cursor"></span>}
      </div>
    );
  }

  // No checkboxes, render normally
  return (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          mark: ({ children, ...props }) => (
            <mark className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded text-gray-900 dark:text-gray-100" {...props}>
              {children}
            </mark>
          )
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  );
};

export const parseTransferContent = (content: string) => {
  try {
    let transferData;

    const cleanContent = content.replace(/'/g, '"');
    transferData = JSON.parse(cleanContent);

    return transferData.token || transferData.agent || 'Unknown';
  } catch (e) {

    const tokenMatch = content.match(/'token':\s*'([^']+)'/);
    if (tokenMatch) {
      return tokenMatch[1];
    }

    const agentMatch = content.match(/'agent':\s*'([^']+)'/);
    if (agentMatch) {
      return `Dispatching to ${agentMatch[1]}`;
    }

    return 'In progress';
  }
};

// Helper function to parse and format progress content
export const parseProgressContent = (content: string) => {
  try {
    let progressData;
    // Parse Python dictionary format
    // Since we know the exact format from the backend, we can use regex extraction
    // This is safer than trying to convert the entire string to JSON
    const agentMatch = content.match(/'agent':\s*'([^']+)'/);
    const toolMatch = content.match(/'tool':\s*'([^']+)'/);
    const stageMatch = content.match(/'stage':\s*'([^']+)'/);
    const rowsMatch = content.match(/'rows':\s*(\d+)/);
    const idMatch = content.match(/'id':\s*'([^']+)'/);
    const typeMatch = content.match(/'type':\s*'([^']+)'/);

    // Extract message field more carefully to handle nested quotes
    let messageMatch = content.match(/'message':\s*"([^"]*(?:[^"\\]|\\.)*[^"\\]?)"/);
    if (!messageMatch) {
      // Fallback for single-quoted messages
      messageMatch = content.match(/'message':\s*'([^']+(?:\\'[^']*)*[^'\\]?)'/);
    }

    progressData = {
      type: typeMatch ? typeMatch[1] : 'progress',
      agent: agentMatch ? agentMatch[1] : 'System',
      tool: toolMatch ? toolMatch[1] : 'process',
      stage: stageMatch ? stageMatch[1] : '',
      message: messageMatch ? messageMatch[1] : '',
      id: idMatch ? idMatch[1] : '',
      rows: rowsMatch ? parseInt(rowsMatch[1]) : undefined
    };

    // Check if developer mode is enabled (URL contains /v)
    const isDeveloperMode = window.location.pathname.includes('/v') ||
      new URLSearchParams(window.location.search).get('v') !== null;

    const agent = progressData.agent || 'System';
    const tool = progressData.tool || 'process';
    const stage = progressData.stage || '';
    const userMessage = progressData.message || ''; // New user-friendly message

    // If we have a user-friendly message, use it
    if (userMessage) {
      // In developer mode, append tool name
      if (isDeveloperMode) {
        return `${userMessage} [${tool}]`;
      }
      return userMessage;
    }

    // Fallback to old format if no user message
    if (stage === 'start') {
      const message = `${agent}: Starting ${tool}`;
      return isDeveloperMode ? `${message} [${tool}]` : message;
    } else if (stage === 'end') {
      const rows = progressData.rows;
      const message = `${agent}: Completed ${tool}${rows ? ` (${rows} results)` : ''}`;
      return isDeveloperMode ? `${message} [${tool}]` : message;
    } else {
      const message = `${agent}: ${tool}`;
      return isDeveloperMode ? `${message} [${tool}]` : message;
    }
  } catch (e) {

    const agentMatch = content.match(/'agent':\s*'([^']+)'/);
    const toolMatch = content.match(/'tool':\s*'([^']+)'/);
    const stageMatch = content.match(/'stage':\s*'([^']+)'/);
    // More robust message extraction that handles nested quotes
    const messageMatch = content.match(/'message':\s*"([^"]*(?:\\"[^"]*)*)"/);

    // Check developer mode
    const isDeveloperMode = window.location.pathname.includes('/v') ||
      new URLSearchParams(window.location.search).get('v') !== null;

    if (messageMatch) {
      const userMessage = messageMatch[1];
      const tool = toolMatch ? toolMatch[1] : '';
      return isDeveloperMode && tool ? `${userMessage} [${tool}]` : userMessage;
    }

    if (agentMatch && toolMatch) {
      const agent = agentMatch[1];
      const tool = toolMatch[1];
      const stage = stageMatch ? stageMatch[1] : '';

      if (stage === 'start') {
        const message = `${agent}: Starting ${tool}`;
        return isDeveloperMode ? `${message} [${tool}]` : message;
      } else if (stage === 'end') {
        const message = `${agent}: Completed ${tool}`;
        return isDeveloperMode ? `${message} [${tool}]` : message;
      } else {
        const message = `${agent}: ${tool}`;
        return isDeveloperMode ? `${message} [${tool}]` : message;
      }
    }
    return 'Processing...';
  }
};

export const getProgressStage = (content: string) => {
  try {
    let progressData;

    const cleanContent = content.replace(/'/g, '"');
    progressData = JSON.parse(cleanContent);

    return progressData.stage || '';
  } catch (e) {
    const stageMatch = content.match(/'stage':\s*'([^']+)'/);
    if (stageMatch) {
      return stageMatch[1];
    }

    return '';
  }
};

// Helper function to extract progress step ID from the actual id field
export const getProgressStepId = (content: string) => {
  try {
    let progressData;
    const cleanContent = content.replace(/'/g, '"');
    progressData = JSON.parse(cleanContent);

    // Use the actual id field if available, fallback to agent:tool
    if (progressData.id) {
      return progressData.id;
    }

    const agent = progressData.agent || 'System';
    const tool = progressData.tool || 'process';
    return `${agent}:${tool}`;
  } catch (e) {
    // Try to extract id first
    const idMatch = content.match(/'id':\s*'([^']+)'/);
    if (idMatch) {
      return idMatch[1];
    }

    // Fallback to agent:tool
    const agentMatch = content.match(/'agent':\s*'([^']+)'/);
    const toolMatch = content.match(/'tool':\s*'([^']+)'/);

    if (agentMatch && toolMatch) {
      const agent = agentMatch[1];
      const tool = toolMatch[1];
      return `${agent}:${tool}`;
    }

    return 'unknown';
  }
};

// Helper function to extract summarization stage from content
export const getSummarizationStage = (content: string) => {
  try {
    const cleanContent = content.replace(/'/g, '"');
    const data = JSON.parse(cleanContent);
    return data.stage || '';
  } catch (e) {
    const stageMatch = content.match(/'stage':\s*'([^']+)'/);
    if (stageMatch) {
      return stageMatch[1];
    }
    return '';
  }
};

// Helper function to extract summarization id from content
export const getSummarizationId = (content: string) => {
  try {
    const cleanContent = content.replace(/'/g, '"');
    const data = JSON.parse(cleanContent);
    return data.id || 'default';
  } catch (e) {
    const idMatch = content.match(/'id':\s*'([^']+)'/);
    if (idMatch) {
      return idMatch[1];
    }
    return 'default';
  }
};

export const extractBoldTitleFromMarkdown = (content: string) => {
  if (!content) return '';
  const match = content.match(/\*\*([^*]+)\*\*/);
  return match ? match[1].trim() : '';
};


export const extractShortMessageFromProgress = (content: string) => {
  if (!content) return '';
  try {
    const cleanContent = content.replace(/'/g, '"');
    const obj = JSON.parse(cleanContent)
    return obj.short_message || '';
  } catch (e) {
    const match = content.match(/'short_message':\s*'([^']+)'/);
    return match ? match[1] : '';
  }
};


export const extractTokenFromTransfer = (content: string) => {
  if (!content) return '';
  try {
    const cleanContent = content.replace(/'/g, '"');
    const obj = JSON.parse(cleanContent);
    return obj.token || '';
  } catch (e) {
    const match = content.match(/'token':\s*'([^']+)'/);
    return match ? match[1] : '';
  }
};

export const getAgentFromProgress = (content: string): string => {
  if (!content) return '';
  try {
    const clean = content.replace(/'/g, '"');
    const obj = JSON.parse(clean);
    return obj.agent || '';
  } catch (e) {
    const m = content.match(/'agent':\s*'([^']+)'/);
    return m ? m[1] : '';
  }
};

export const getAgentFromTransfer = (content: string): string => {
  if (!content) return '';
  try {
    const clean = content.replace(/'/g, '"');
    const obj = JSON.parse(clean);
    return obj.agent || '';
  } catch (e) {
    const m = content.match(/'agent':\s*'([^']+)'/);
    return m ? m[1] : '';
  }
};

export const getStepInfo = (stepType: string | undefined) => {
  switch (stepType) {
    case 'thinking':
      return { icon: HiOutlineLightBulb, label: 'Thinking', color: '#6b7280' };
    case 'research':
      return { icon: HiOutlineGlobeAlt, label: 'Web Search', color: '#6b7280' };
    case 'progress':
      return { icon: HiOutlineCog, label: 'Processing', color: '#6b7280' };
    case 'transfer':
      return { icon: HiOutlineRefresh, label: 'Transfer', color: '#6b7280' };
    case 'summarization':
      return { icon: HiOutlineSparkles, label: 'Summarizing', color: '#7c3aed' };
    default:
      return { icon: HiOutlineLightBulb, label: 'Processing', color: '#6b7280' };
  }
};

export const getEnhancedPreview = (tag: ActiveTag) => {
  if (!tag.content) return null;

  let content = tag.content;

  // For different step types, parse the content appropriately
  if (tag.type === 'progress') {
    const startText = parseProgressContent(content);

    return {
      title: startText,
      type: 'status'
    };
  } else if (tag.type === 'transfer') {
    return {
      title: parseTransferContent(content),
      type: 'status'
    };
  } else if (tag.type === 'summarization') {
    return {
      title: parseTransferContent(content),
      type: 'status'
    };
  } else if (tag.type === 'thinking') {
    // For thinking, show the full content instead of just a preview
    content = content.replace(/<\/?thinking>/g, '');
    content = content.replace(/^\s*[\□☑✓]\s*/gm, '');
    content = content.replace(/^\s*\[[x\sX]\]\s*/gm, '');

    // Return full content for thinking steps
    return {
      title: 'Thinking',
      content: content.trim(),
      type: 'full_content'
    };
  } else if (tag.type === 'research') {
    // For research, show structured preview
    content = content.replace(/<\/?research>/g, '');
    content = content.replace(/^\s*[\□☑✓]\s*/gm, '');
    content = content.replace(/^\s*\[[x\sX]\]\s*/gm, '');

    const lines = content.split('\n').filter(line => line.trim());

    // Look for key sections and structure
    const preview: any[] = [];
    let currentSection: any = null;

    lines.forEach(line => {
      // Check for section headers (lines that end with colon or look like headers)
      if (line.match(/^#+\s/) || line.match(/^[A-Z][^:]*:$/) || line.match(/^\*\*[^*]+\*\*$/)) {
        const title = line.replace(/^#+\s/, '').replace(/\*\*/g, '').replace(/:$/, '');
        if (title.length < 50) {
          currentSection = { title, content: [] };
          preview.push(currentSection);
        }
      } else if (currentSection && line.length > 10) {
        currentSection.content.push(line);
      } else if (!currentSection && line.length > 10) {
        preview.push({ title: 'Analysis', content: [line] });
        currentSection = preview[preview.length - 1];
      }
    });

    if (preview.length > 0) {
      const firstSection = preview[0];
      const contentLines = firstSection.content.slice(0, 3).map((line: any) =>
        line.length > 80 ? line.substring(0, 80) + '...' : line
      );

      return {
        title: firstSection.title,
        content: contentLines.length > 0 ? contentLines.join('\n') : null,
        type: 'structured'
      };
    }

    // Fallback for research
    const contentLines = lines.slice(0, 4).map(line =>
      line.length > 80 ? line.substring(0, 80) + '...' : line
    );

    return {
      title: 'Web Search',
      content: contentLines.length > 0 ? contentLines.join('\n') : null,
      type: 'structured'
    };
  }

  return null;
};

const parseResearchMeta = (raw: string): { id?: string; stage?: string } => {
  if (!raw) return {};
  const idMatch = raw.match(/["']id["']\s*:\s*["']([^"']+)["']/);
  const stageMatch = raw.match(/["']stage["']\s*:\s*["']([^"']+)["']/);
  return { id: idMatch ? idMatch[1] : undefined, stage: stageMatch ? stageMatch[1] : undefined };
};

export const mergeResearchById = (tags: Tag[]): Tag[] => {
  const groups: Record<string, { content: string; hasEnd: boolean; firstIndex: number; isStreaming: boolean }> = {};

  tags.forEach((tag, index) => {
    if (tag.type !== 'research' || !tag?.content) return;
    const { id, stage } = parseResearchMeta(tag.content);
    if (!id) return;
    const g = groups[id] || { content: '', hasEnd: false, firstIndex: index, isStreaming: true };
    g.content += (g.content ? '\n' : '') + tag.content;
    if (stage === 'end') {
      g.hasEnd = true;
      g.isStreaming = false;
    }
    if (index < g.firstIndex) g.firstIndex = index;
    groups[id] = g;
  });

  const emitted = new Set<string>();
  const out: Tag[] = [];
  tags.forEach((tag) => {
    if (tag.type !== 'research') {
      out.push(tag);
      return;
    }
    const { id } = parseResearchMeta(tag.content);
    if (!id) {
      out.push(tag);
      return;
    }
    if (emitted.has(id)) {
      return;
    }
    emitted.add(id);
    const g = groups[id];
    if (!g) {
      out.push(tag);
      return;
    }
    out.push({ ...tag, content: g.content, isStreaming: g.isStreaming });
  });

  return out;
};